import { useState, useMemo } from "react";
import { useParams } from "react-router-dom";

import { FilterList } from "@mui/icons-material";
import { Badge, Box, Button, FormControlLabel, Grid, IconButton, Popover, Stack, Switch, Tooltip, Typography } from "@mui/material";
import { clientTheme } from "../../../theme/clientTheme";

const isModernTheme = clientTheme.layout?.portfolioCardVariant === "modern";

import { ProgrammeSummary } from "../components/ProgrammeSummary";
import { DeliveryPerformance } from "../components/DeliveryPerformance";
import { RisksAndIssues } from "../components/RisksAndIssues";
import { MonthlySpendTrendChart } from "../components/MonthlySpendTrendChart";
import { VarianceAnalysisChart } from "../components/VarianceAnalysisChart";
import { AggregatedGateView } from "../components/AggregatedGateView";

import { useProgrammeSummary } from "../hooks/useProgrammeSummary";
import { useDeliveryPerformance } from "../hooks/useDeliveryPerformance";
import { usePhaseCounts } from "../hooks/usePhaseCounts";
import { useRisks } from "../hooks/useRisks";
import { useMonthlySpendTrend } from "../hooks/useMonthlySpendTrend";
import { useVarianceAnalysis } from "../hooks/useVarianceAnalysis";

import { DataLayout, DrawerControl, FilterSideBar, MetaDrivenTable, StatusChip } from "../../../shared/components";
import { useFilterContext } from "../../../shared/context";
import { useActiveFilterLabels } from "../../../shared/hooks";
import { useFetchPortfolioActivityList } from "../hooks/useFetchPortfolioActivityList";
import { Box as MuiBox, CircularProgress } from "@mui/material";
import { ColumnFilter, FilterOption } from "../../tasks/components/columnFilter";
import { useDisplayField } from "../../../hooks/useDisplayField";
import { useInfiniteStakeholders } from "../../projects/hooks/useStakeholders";

import { SearchField } from "../../../shared/components";
import { useDebounce } from "../../../hooks/useDebounce";

export const ProgrammeDashboard = () => {

  const { portfolioId } = useParams();
  const { quickFilters } = useFilterContext();
  const { hasActiveFilters, activeFilterLabels } = useActiveFilterLabels();
  const [isSwitched, setIsSwitched] = useState(false);
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const handleOpenFilter = () => setIsOpenFilter(true);
  const handleCloseFilter = () => setIsOpenFilter(false);

  const {
    metrics: programmeMetrics,
    isLoading: isProgrammeLoading,
    isError: isProgrammeError,
    error: programmeError
  } = useProgrammeSummary({ portfolio_id: portfolioId, quickFilters });

  const {
    metrics: deliveryMetrics,
    isLoading: isDeliveryLoading,
    isError: isDeliveryError,
    error: deliveryError,
  } = useDeliveryPerformance({ portfolio_id: portfolioId, quickFilters });

  const {
    gates,
    isLoading: isGatesLoading,
    isError: isGatesError,
    error: gatesError,
  } = usePhaseCounts({ portfolio_id: portfolioId, quickFilters });

  const {
    chartData: monthlySpendData,
    portfolioNames,
    isLoading: isLoadingMonthlySpend,
    isError: isErrorMonthlySpend,
    error: errorMonthlySpend,
  } = useMonthlySpendTrend({ portfolio_id: portfolioId, quickFilters });

  const {
    chartData: varianceData,
    isLoading: isLoadingVariance,
    isError: isErrorVariance,
    error: errorVariance,
  } = useVarianceAnalysis({ portfolio_id: portfolioId, quickFilters });

  const {
    risks: timeScheduleRisks,
    isLoading: isLoadingTimeSchedule,
    isError: isErrorTimeSchedule,
    error: errorTimeSchedule,
  } = useRisks("Time/Schedule", { portfolio_id: portfolioId, quickFilters });

  const {
    risks: costCommercialRisks,
    isLoading: isLoadingCostCommercial,
    isError: isErrorCostCommercial,
    error: errorCostCommercial,
  } = useRisks("Cost/Commercial", { portfolio_id: portfolioId, quickFilters });

  const {
    risks: safetyHseqRisks,
    isLoading: isLoadingSafetyHseq,
    isError: isErrorSafetyHseq,
    error: errorSafetyHseq,
  } = useRisks("Safety/HSEQ", { portfolio_id: portfolioId, quickFilters });

  const { getFilterOptions } = useDisplayField([]);

  const { stakeholders } = useInfiniteStakeholders({ search: '' });

  const ownerOptions: FilterOption[] = useMemo(
    () => stakeholders.map((s: any) => ({ key: String(s.user_id), display_text: s.full_name })),
    [stakeholders]
  );

  const [activityFilterAnchorEl, setActivityFilterAnchorEl] = useState<Record<string, HTMLElement | null>>({});
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [activityOwnerFilter, setActivityOwnerFilter] = useState<string[]>([]);
  const [activitySearchDraft, setActivitySearchDraft] = useState('');
  const [activitySearch, setActivitySearch] = useState('');
  const debouncedActivitySearch = useDebounce(activitySearchDraft, 500);

  const appliedFilters = useMemo(() => ({
    ...(categoryFilter.length > 0 ? { category: categoryFilter } : {}),
    ...(activityOwnerFilter.length > 0 ? { owner: activityOwnerFilter } : {}),
    ...(debouncedActivitySearch ? { search_term: debouncedActivitySearch } : {}),
  }), [categoryFilter, activityOwnerFilter, debouncedActivitySearch]);

  const { data: portfolioActivityList, isLoading: isActivityListLoading } = useFetchPortfolioActivityList(
    clientTheme.layout?.showPortfolioActivityList ? portfolioId : undefined,
    Object.keys(appliedFilters).length > 0 ? appliedFilters : undefined
  );

  // Fetch unfiltered data once for populating owner filter options
  const { data: allActivityData } = useFetchPortfolioActivityList(
    clientTheme.layout?.showPortfolioActivityList ? portfolioId : undefined
  );

  const categoryOptions: FilterOption[] = useMemo(() =>
    getFilterOptions('ACTIVITY_CATEGORY'),
    [getFilterOptions]
  );

  const createActivityFilterHeader = (label: string, columnKey: string, isActive: boolean) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <span>{label}</span>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setActivityFilterAnchorEl((prev) => ({ ...prev, [columnKey]: e.currentTarget }));
        }}
        sx={{ p: 0.5, ml: 'auto', pointerEvents: 'auto' }}
      >
        <Badge
          variant="dot"
          invisible={!isActive}
          color="error"
          sx={{ '& .MuiBadge-dot': { width: 8, height: 8, minWidth: 8 } }}
        >
          <FilterList fontSize="small" />
        </Badge>
      </IconButton>
    </Box>
  );

  return (
    <Box sx={{ p: 2 }} display={'flex'} flexDirection={'column'}>

       <Tooltip
            title={
              hasActiveFilters ? (
                <Box sx={{ p: 0.5 }}>
                  {activeFilterLabels.map((label, i) => (
                    <Typography key={i} variant="caption" display="block">
                      {label}
                    </Typography>
                  ))}
                </Box>
              ) : ""
            }
            placement="bottom-start"
            disableHoverListener={!hasActiveFilters}
            arrow
          >
            <Button
              startIcon={<FilterList />}
              variant={isModernTheme ? "outlined" : (hasActiveFilters ? "contained" : "text")}
              color="inherit"
              onClick={handleOpenFilter}
              sx={
                isModernTheme
                  ? {
                    marginLeft: "auto",
                    borderRadius: 2,
                    textTransform: "none",
                    borderColor: "divider",
                    color: hasActiveFilters ? "primary.main" : "text.primary",
                    whiteSpace: "nowrap",
                  }
                  : hasActiveFilters
                  ? {
                    marginLeft: "auto",
                    backgroundColor: "action.selected",
                    color: "text.primary",
                    "&:hover": { backgroundColor: "action.hover" },
                  }
                  : { marginLeft: "auto" }
              }
            >
              {isModernTheme ? `Filters${hasActiveFilters ? ` (${activeFilterLabels.length})` : ""}` : `FILTERS${hasActiveFilters ? ` (${activeFilterLabels.length})` : ""}`}
            </Button>
          </Tooltip>



      <Stack spacing={2}>

        <AggregatedGateView
          gates={gates}
          isLoading={isGatesLoading}
          isError={isGatesError}
          error={gatesError}
        />

        <Grid container spacing={2}>
          {!clientTheme.layout?.hidePortfolioSummary && (
            <Grid size={{ xs: 12, md: 6 }}>
              <ProgrammeSummary
                metrics={programmeMetrics}
                isLoading={isProgrammeLoading}
                isError={isProgrammeError}
                error={programmeError}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, md: clientTheme.layout?.hidePortfolioSummary ? 12 : 6 }}>
            <DeliveryPerformance
              metrics={deliveryMetrics}
              isLoading={isDeliveryLoading}
              isError={isDeliveryError}
              error={deliveryError}
            />
          </Grid>
        </Grid>

        <RisksAndIssues
          timeScheduleRisks={timeScheduleRisks}
          costCommercialRisks={costCommercialRisks}
          safetyHseqRisks={safetyHseqRisks}
          isLoadingTimeSchedule={isLoadingTimeSchedule}
          isLoadingCostCommercial={isLoadingCostCommercial}
          isLoadingSafetyHseq={isLoadingSafetyHseq}
          isErrorTimeSchedule={isErrorTimeSchedule}
          isErrorCostCommercial={isErrorCostCommercial}
          isErrorSafetyHseq={isErrorSafetyHseq}
          errorTimeSchedule={errorTimeSchedule}
          errorCostCommercial={errorCostCommercial}
          errorSafetyHseq={errorSafetyHseq}
        />

        {clientTheme.layout?.showPortfolioActivityList && (
          <DataLayout title="Activity List">
            <MuiBox px={2} pt={2} pb={1}>
              <SearchField
                label="Search activities..."
                searchTerm={activitySearchDraft}
                handleSearch={(e) => setActivitySearchDraft(e.target.value)}
                handleClearSearch={() => { setActivitySearchDraft(''); }}
                handleApplySearch={(val) => setActivitySearch(val)}
                size="small"
              />
            </MuiBox>
            {isActivityListLoading ? (
              <MuiBox display="flex" alignItems="center" justifyContent="center" py={4}>
                <CircularProgress size={24} />
              </MuiBox>
            ) : (
              <MetaDrivenTable
                headers={[
                  { alias_name: "rag", label: "RAG", display_order: 1, is_show: true },
                  { alias_name: "activity_id", label: "ID", display_order: 2, is_show: true },
                  { alias_name: "activity_name", label: "Activity Name", display_order: 3, is_show: true },
                  { alias_name: "project_description", label: "Project Description", display_order: 4, is_show: true },
                  { alias_name: "owner_name", label: createActivityFilterHeader("Owner", "owner", activityOwnerFilter.length > 0), display_order: 5, is_show: true },
                  { alias_name: "category", label: createActivityFilterHeader("Category", "category", categoryFilter.length > 0), display_order: 6, is_show: true },
                  { alias_name: "status_display_text", label: "Status", display_order: 7, is_show: true },
                  { alias_name: "phase_name", label: "Phase", display_order: 8, is_show: true },
                  { alias_name: "region_description", label: "Region", display_order: 9, is_show: true },
                  { alias_name: "days_in_phase", label: "Days in Phase", display_order: 10, is_show: true },
                  { alias_name: "query_count", label: "Queries", display_order: 11, is_show: true },
                  { alias_name: "materials", label: "Materials", display_order: 12, is_show: true },
                ]}
                data={portfolioActivityList}
                dataMetrics={{
                  rag: {
                    type: "component",
                    element: (value: { rag: string }) => (
                      <MuiBox
                        sx={{
                          width: 12,
                          height: 12,
                          bgcolor:
                            value.rag === "amber"
                              ? "warning.main"
                              : value.rag === "green"
                                ? "success.main"
                                : "error.main",
                          borderRadius: "50%",
                        }}
                      />
                    ),
                  },
                  status_display_text: {
                    type: "component",
                    element: (value: { status_display_text: string }) => (
                      <StatusChip
                        label={value.status_display_text}
                        color={
                          value.status_display_text === "In Progress"
                            ? "amber"
                            : value.status_display_text === "Completed" || value.status_display_text === "Complete"
                              ? "green"
                              : "red"
                        }
                        variant="filled"
                      />
                    ),
                  },
                  query_count: {
                    type: "component",
                    element: (value: { query_count: number }) => (
                      <StatusChip
                        label={String(value.query_count)}
                        color="blue"
                        variant="filled"
                      />
                    ),
                  },
                  materials: {
                    type: "component",
                    element: (value: { materials: string }) => (
                      <StatusChip
                        label={String(value.materials) || "N/A"}
                        color={
                          value.materials === "Confirmed"
                            ? "green"
                            : value.materials === "Pending"
                              ? "amber"
                              : "red"
                        }
                        variant="filled"
                      />
                    ),
                  },
                }}
                sortDisabled
              />
            )}

            <Popover
              open={Boolean(activityFilterAnchorEl['category'])}
              anchorEl={activityFilterAnchorEl['category']}
              onClose={() => setActivityFilterAnchorEl(prev => ({ ...prev, category: null }))}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <ColumnFilter
                columnLabel="Category"
                selected={categoryFilter}
                options={categoryOptions}
                onApply={(keys) => { setCategoryFilter(keys); setActivityFilterAnchorEl(prev => ({ ...prev, category: null })); }}
                onClose={() => setActivityFilterAnchorEl(prev => ({ ...prev, category: null }))}
              />
            </Popover>

            <Popover
              open={Boolean(activityFilterAnchorEl['owner'])}
              anchorEl={activityFilterAnchorEl['owner']}
              onClose={() => setActivityFilterAnchorEl(prev => ({ ...prev, owner: null }))}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <ColumnFilter
                columnLabel="Owner"
                selected={activityOwnerFilter}
                options={ownerOptions}
                searchable
                onApply={(keys) => {
                  setActivityOwnerFilter(keys);
                  setActivityFilterAnchorEl(prev => ({ ...prev, owner: null }));
                }}
                onClose={() => setActivityFilterAnchorEl(prev => ({ ...prev, owner: null }))}
              />
            </Popover>
          </DataLayout>
        )}

        <DataLayout
          bgVariant="filled"
          title={isSwitched ? "Variance Analysis" : "Monthly spend trend by portfolio"} action={
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={isSwitched}
                  onChange={() => setIsSwitched(!isSwitched)}
                />
              }
              label={
                isSwitched ? "Show Monthly Spend Trend" : "Show Variance Analysis"
              }
            />
          }>
          {
            !isSwitched ? (
              <MonthlySpendTrendChart
                data={monthlySpendData}
                portfolioNames={portfolioNames}
                isLoading={isLoadingMonthlySpend}
                isError={isErrorMonthlySpend}
                error={errorMonthlySpend}
              />
            ) : (
              <VarianceAnalysisChart
                data={varianceData}
                isLoading={isLoadingVariance}
                isError={isErrorVariance}
                error={errorVariance}
              />
            )
          }
        </DataLayout>

      </Stack>

      <DrawerControl
        title="Filter Records"
        subTitle="Please apply following filters to refine results"
        isOpen={isOpenFilter}
        handleClose={handleCloseFilter}
      >
        <FilterSideBar hiddenFilters={["stakeholder"]} onClose={handleCloseFilter} />
      </DrawerControl>

    </Box>
  );
};
